/**
 * useSnackbar Hook
 * Hook for showing toast notifications
 */

import { create } from 'zustand';

export interface SnackbarMessage {
  id: string;
  message: string;
  description?: string;
  severity: 'success' | 'warning' | 'error' | 'info';
  duration?: number;
}

export interface SnackbarOptions {
  description?: string;
  duration?: number;
}

interface SnackbarState {
  snackbars: SnackbarMessage[];
  showSnackbar: (
    message: string,
    severity?: SnackbarMessage['severity'],
    options?: SnackbarOptions
  ) => void;
  removeSnackbar: (id: string) => void;
}

export const useSnackbarStore = create<SnackbarState>((set) => ({
  snackbars: [],

  showSnackbar: (message, severity = 'info', options = {}) => {
    const duration = options.duration ?? 6000;
    const id = `snackbar-${Date.now()}-${Math.random()}`;
    const snackbar: SnackbarMessage = {
      id,
      message,
      description: options.description,
      severity,
      duration,
    };

    set((state) => ({
      snackbars: [...state.snackbars, snackbar],
    }));

    // Auto-dismiss after duration
    if (duration > 0) {
      setTimeout(() => {
        set((state) => ({
          snackbars: state.snackbars.filter((s) => s.id !== id),
        }));
      }, duration);
    }
  },

  removeSnackbar: (id) =>
    set((state) => ({
      snackbars: state.snackbars.filter((s) => s.id !== id),
    })),
}));

/**
 * Hook to show toast notifications
 *
 * @example
 * ```tsx
 * const snackbar = useSnackbar();
 *
 * snackbar.success('Changes saved!');
 * snackbar.error('Failed to save changes');
 * snackbar.warning('Please review your input');
 * snackbar.info('New updates available');
 * ```
 */
export function useSnackbar() {
  const { showSnackbar, removeSnackbar } = useSnackbarStore();

  return {
    success: (message: string, options?: SnackbarOptions) =>
      showSnackbar(message, 'success', options),
    error: (message: string, options?: SnackbarOptions) => showSnackbar(message, 'error', options),
    warning: (message: string, options?: SnackbarOptions) =>
      showSnackbar(message, 'warning', options),
    info: (message: string, options?: SnackbarOptions) => showSnackbar(message, 'info', options),
    dismiss: removeSnackbar,
  };
}
